import { Text, Title } from "@clipboard-health/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Container, Stack, TextareaAutosize, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

const workedBreakAuthorizationSchema = z.object({
  reason: z
    .string({
      errorMap: () => ({
        message: "Please enter a detailed explanation that is between 60 and 300 characters.",
      }),
    })
    .min(60)
    .max(300),
});
export type WorkedBreakAuthorization = z.infer<typeof workedBreakAuthorizationSchema>;

export interface WorkedBreakAuthorizationPageProps {
  onCancel: () => void;
  onSubmit: (data: WorkedBreakAuthorization) => void;
  facilityName?: string;
}

export function WorkedBreakAuthorizationPage(
  props: WorkedBreakAuthorizationPageProps
): JSX.Element {
  const { onCancel, onSubmit, facilityName } = props;

  const formMethods = useForm<WorkedBreakAuthorization>({
    resolver: zodResolver(workedBreakAuthorizationSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const reason = register("reason");

  return (
    <Container maxWidth="xl" sx={{ height: "100%" }}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing={2} sx={{ textSizeAdjust: "100%" }}>
            <Title component="h1">Why didn&apos;t you take a complete break?</Title>
            <Text bold>
              {facilityName ?? "The facility"} requires that you take a 30-minute unpaid break.
            </Text>
            <Text>
              If you were unable to take a full 30-minute break due to work demands, please provide
              a detailed explanation here in order to be compensated for worked-through break time.
              If a full-time staff member directed you to skip part or all of your break, we
              recommend you include their name, title, and any relevant details.
            </Text>
            <Text>
              Payments for worked break time undergo an additional three business day approval
              process where your explanation provided here is reviewed by{" "}
              {facilityName ?? "the facility"}.{" "}
              <u>
                <b>Note that this is separate from standard shift payment processing.</b>
              </u>
            </Text>
            <Box paddingY={2}>
              <TextField
                {...reason}
                fullWidth
                label="Reason *"
                InputProps={{ inputComponent: TextareaAutosize }}
                error={Boolean(errors.reason?.message)}
                helperText={errors.reason?.message}
              />
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2} padding={2}>
            <Button fullWidth variant="outlined" size="small" onClick={onCancel}>
              Go Back
            </Button>
            <Button fullWidth variant="contained" type="submit" size="small">
              Confirm
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Container>
  );
}
